import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth as authApi, business as bizApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Handles the Google OAuth redirect at /auth/google?code=...
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const err = params.get("error");

    if (err) {
      const friendly = err === "access_denied"
        ? "Google sign-in cancelled"
        : err === "redirect_uri_mismatch"
        ? "Google sign-in isn't configured for this domain yet — use email & password"
        : `Google sign-in error: ${err}`;
      toast.error(friendly);
      nav("/login", { replace: true });
      return;
    }
    if (!code) {
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const redirectUri = window.location.origin + "/auth/google";
        const { data } = await authApi.googleCallback(code, redirectUri);
        if (data.session_token) localStorage.setItem("rq_token", data.session_token);
        await refresh();
        toast.success("Signed in with Google");

        // Route new users straight to /onboarding, existing to /dashboard.
        let dest = "/dashboard";
        try {
          const { data: biz } = await bizApi.get();
          if (!biz?.onboarded) dest = "/onboarding";
        } catch { dest = "/onboarding"; }
        nav(dest, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Google sign-in failed. Try again.");
        nav("/login", { replace: true });
      }
    })();
  }, [location.search, nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
      <div className="flex flex-col items-center gap-3 text-[#001F3F]">
        <Loader2 className="animate-spin text-[#2563EB]" size={32} />
        <div className="text-sm text-[#64748B]">Finishing Google sign-in…</div>
      </div>
    </div>
  );
}
